// Client HTTP de l'API (préfixe /api/v1, même origine via le relais de Vite ou du mandataire).
// Le jeton d'accès (15 minutes) reste en mémoire, jamais dans le stockage du navigateur ;
// le jeton de rafraîchissement est un cookie HttpOnly, accompagné d'un cookie CSRF lisible
// renvoyé dans l'en-tête x-csrf-jeton (double soumission).
import { ErreurApi } from './erreurs';

export const PREFIXE_API = '/api/v1';

type Ecouteur = (evenement: 'session_expiree' | 'jeton') => void;

let jetonAcces: string | null = null;
let expirationAcces = 0;
let rafraichissementEnCours: Promise<boolean> | null = null;
let minuterie: number | null = null;
const ecouteurs = new Set<Ecouteur>();

export function ecouterSession(e: Ecouteur): () => void {
  ecouteurs.add(e);
  return () => ecouteurs.delete(e);
}

function emettre(evenement: 'session_expiree' | 'jeton') {
  for (const e of ecouteurs) e(evenement);
}

export function jetonPresent(): boolean {
  return jetonAcces !== null;
}

/** Enregistre un jeton d'accès et planifie son rafraîchissement une minute avant l'expiration. */
export function definirJeton(jeton: string | null, expireDansSecondes = 900): void {
  jetonAcces = jeton;
  expirationAcces = jeton ? Date.now() + expireDansSecondes * 1000 : 0;
  if (minuterie !== null) window.clearTimeout(minuterie);
  minuterie = null;
  if (jeton) {
    const delai = Math.max(10_000, expireDansSecondes * 1000 - 60_000);
    minuterie = window.setTimeout(() => void rafraichir(), delai);
  }
  emettre('jeton');
}

function lireCookie(nom: string): string | null {
  const ligne = document.cookie.split('; ').find((c) => c.startsWith(`${nom}=`));
  return ligne ? decodeURIComponent(ligne.slice(nom.length + 1)) : null;
}

async function lireErreur(reponse: Response): Promise<ErreurApi> {
  try {
    const corps = (await reponse.json()) as { code?: string; message?: string; details?: unknown };
    return new ErreurApi(reponse.status, corps.code ?? 'erreur', corps.message ?? `Erreur ${reponse.status}`, corps.details);
  } catch {
    return new ErreurApi(reponse.status, 'erreur', `Erreur ${reponse.status}`);
  }
}

/**
 * Rafraîchit le jeton d'accès avec le cookie HttpOnly. Une seule tentative à la fois.
 * Retourne false si la session n'est plus valide.
 */
export function rafraichir(): Promise<boolean> {
  if (rafraichissementEnCours) return rafraichissementEnCours;
  rafraichissementEnCours = (async () => {
    const csrf = lireCookie('cc_csrf');
    if (!csrf) return false;
    try {
      const r = await fetch(`${PREFIXE_API}/auth/rafraichir`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'x-csrf-jeton': csrf },
      });
      if (!r.ok) return false;
      const corps = (await r.json()) as { jeton_acces: string; expire_dans: number };
      definirJeton(corps.jeton_acces, corps.expire_dans);
      return true;
    } catch {
      return false;
    }
  })().finally(() => {
    rafraichissementEnCours = null;
  });
  return rafraichissementEnCours;
}

export interface OptionsRequete {
  methode?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  corps?: unknown;
  signal?: AbortSignal;
  /** Paramètres de requête ; les valeurs vides sont ignorées. */
  parametres?: Record<string, string | number | boolean | null | undefined>;
  /** Retourne la réponse brute (téléchargements). */
  brut?: boolean;
}

export function construireChemin(chemin: string, parametres?: OptionsRequete['parametres']): string {
  const p = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(parametres ?? {})) {
    if (valeur !== undefined && valeur !== null && valeur !== '') p.set(cle, String(valeur));
  }
  const requete = p.toString();
  return `${PREFIXE_API}${chemin}${requete ? `?${requete}` : ''}`;
}

const CODES_JETON = new Set(['jeton_invalide', 'jeton_perime', 'session_absente']);

async function envoyer(chemin: string, o: OptionsRequete): Promise<Response> {
  const entetes: Record<string, string> = {};
  if (jetonAcces) entetes.authorization = `Bearer ${jetonAcces}`;
  let corps: BodyInit | undefined;
  if (o.corps instanceof FormData) corps = o.corps;
  else if (o.corps !== undefined) {
    entetes['content-type'] = 'application/json';
    corps = JSON.stringify(o.corps);
  }
  try {
    return await fetch(construireChemin(chemin, o.parametres), {
      method: o.methode ?? 'GET',
      headers: entetes,
      body: corps,
      signal: o.signal,
      credentials: 'same-origin',
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ErreurApi(0, 'hors_ligne', 'Le serveur est injoignable : vérifiez la connexion.');
  }
}

/** Requête authentifiée ; un jeton expiré est rafraîchi une fois, puis la session est déclarée expirée. */
export async function requete<T>(chemin: string, o: OptionsRequete = {}): Promise<T> {
  if (jetonAcces && Date.now() > expirationAcces - 5_000) await rafraichir();
  let reponse = await envoyer(chemin, o);
  if (reponse.status === 401 && jetonAcces) {
    const erreur = await lireErreur(reponse.clone());
    if (CODES_JETON.has(erreur.code) && (await rafraichir())) {
      reponse = await envoyer(chemin, o);
    }
    if (reponse.status === 401) {
      definirJeton(null);
      emettre('session_expiree');
      throw new ErreurApi(401, 'session_expiree', 'Votre session a expiré : reconnectez-vous.');
    }
  }
  if (!reponse.ok) throw await lireErreur(reponse);
  if (o.brut) return reponse as unknown as T;
  if (reponse.status === 204) return undefined as T;
  const type = reponse.headers.get('content-type') ?? '';
  return (type.includes('json') ? await reponse.json() : await reponse.text()) as T;
}

/** Requête publique (connexion, second facteur) : pas de jeton, erreurs rendues telles quelles. */
export async function requetePublique<T>(chemin: string, corps: unknown): Promise<T> {
  let r: Response;
  try {
    r = await fetch(construireChemin(chemin), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(corps),
      credentials: 'same-origin',
    });
  } catch {
    throw new ErreurApi(0, 'hors_ligne', 'Le serveur est injoignable : vérifiez la connexion.');
  }
  if (!r.ok) throw await lireErreur(r);
  return (await r.json()) as T;
}
